import React from 'react';
import { AlertTriangle, Check, CheckCircle2, Eye, FileText, Info } from 'lucide-react';

/**
 * Carte « exemple de rapport » — le produit montré avant le dépôt.
 *
 * Reprend la maquette « Refonte conversion » (planches 1, 2 et 4). Le dossier est
 * FICTIF et la mention doit rester visible : c'est une contrainte de la maquette,
 * pas une décoration.
 *
 * N'utilise pas la classe .card : App.css lui impose background:white et écrase
 * les fonds Tailwind des blocs internes.
 */

// Demi-cercle de rayon 66 → longueur ≈ 207. La portion colorée vaut le pourcentage.
const Gauge = ({ value = 68, compact = false }) => {
  const r = compact ? 48 : 66;
  const w = compact ? 124 : 168;
  const h = compact ? 72 : 96;
  const cy = compact ? 65 : 88;
  const x1 = compact ? 14 : 18;
  const x2 = compact ? 110 : 150;
  const len = Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: w, height: h }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path
          d={`M${x1} ${cy} A${r} ${r} 0 0 1 ${x2} ${cy}`}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={compact ? 11 : 14}
          strokeLinecap="round"
        />
        <path
          d={`M${x1} ${cy} A${r} ${r} 0 0 1 ${x2} ${cy}`}
          fill="none"
          stroke="#16a34a"
          strokeWidth={compact ? 11 : 14}
          strokeLinecap="round"
          strokeDasharray={`${(len * value) / 100} ${len}`}
        />
      </svg>
      <div className="absolute left-0 right-0 flex flex-col items-center" style={{ bottom: compact ? 1 : 2 }}>
        <div
          className="font-extrabold leading-none"
          style={{ color: '#15803d', fontSize: compact ? 26 : 34 }}
        >
          {value}
          <span className="font-bold" style={{ fontSize: compact ? 14 : 18 }}>%</span>
        </div>
        <div
          className="font-semibold uppercase tracking-wider"
          style={{ color: '#64748b', fontSize: 11 }}
        >
          Contestable
        </div>
      </div>
    </div>
  );
};

const FINDINGS = [
  {
    icon: AlertTriangle,
    titre: 'À surveiller — 3 points',
    texte:
      "Le montant de 412,50 € est présenté en bloc, sans ventilation entre la somme d'origine et les frais ajoutés.",
    bg: '#fffbeb', border: '#fde68a', titleColor: '#92400e', textColor: '#78350f', iconColor: '#b45309',
  },
  {
    icon: Info,
    titre: 'Ce qui reste flou — 2 points',
    texte:
      "La date d'émission de la facture est inconnue. C'est elle qui détermine si le délai de prescription est dépassé.",
    bg: '#eff6ff', border: '#bfdbfe', titleColor: '#1e40af', textColor: '#1e3a8a', iconColor: '#1d4ed8',
  },
  {
    icon: FileText,
    titre: 'Documents à retrouver — 4',
    texte: "La facture d'origine, le contrat, le premier courrier reçu et vos relevés de paiement.",
    bg: '#f8fafc', border: '#e2e8f0', titleColor: '#334155', textColor: '#475569', iconColor: '#475569',
  },
  {
    icon: CheckCircle2,
    titre: 'Ce que vous pouvez faire maintenant',
    texte: 'Ne rien payer et demander les justificatifs par écrit. Le courrier type est fourni.',
    bg: '#f0fdf4', border: '#bbf7d0', titleColor: '#15803d', textColor: '#166534', iconColor: '#16a34a',
  },
];

export const ExempleRapportBadge = ({ className = '' }) => (
  <div
    className={`inline-flex items-center gap-[7px] rounded-full px-3 py-[6px] text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-white ${className}`}
    style={{ backgroundColor: '#1e293b' }}
  >
    <Eye size={12} strokeWidth={2.5} />
    Exemple de rapport
  </div>
);

export const MentionFictif = ({ className = '' }) => (
  <div className={`flex items-center justify-center gap-[7px] text-xs sm:text-[13px] ${className}`} style={{ color: '#475569' }}>
    <Info size={14} className="shrink-0" />
    Dossier fictif présenté à titre d'illustration
  </div>
);

const ReportPreviewCard = ({ compact = false, sousTitre = 'Dossier #DEMO · analysé en 2 min 14 s' }) => (
  <div
    className="overflow-hidden rounded-2xl border bg-white"
    style={{
      borderColor: '#e2e8f0',
      boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.18), 0 8px 16px -8px rgba(15, 23, 42, 0.08)',
    }}
  >
    <div
      className="flex items-center justify-between border-b px-4 py-3 sm:px-6 sm:py-[18px]"
      style={{ borderColor: '#f1f5f9', backgroundColor: '#f8fafc' }}
    >
      <div className="flex flex-col gap-[3px]">
        <div className="text-[13px] sm:text-sm font-bold" style={{ color: '#1e3a8a' }}>
          Diagnostic de votre dossier
        </div>
        <div className="text-[11px] sm:text-xs" style={{ color: '#64748b' }}>{sousTitre}</div>
      </div>
      <div
        className="flex items-center gap-[5px] rounded-full px-[10px] py-1 text-[11px] sm:text-xs font-semibold"
        style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
      >
        <Check size={12} strokeWidth={3} />
        Gratuit
      </div>
    </div>

    <div className="flex items-center gap-4 p-4 sm:gap-[26px] sm:p-6">
      <Gauge value={68} compact={compact} />
      <div className="flex flex-col gap-[5px] sm:gap-2">
        <div className="text-[13.5px] sm:text-[15px] font-bold" style={{ color: '#1e293b' }}>
          <span className="sm:hidden">Des angles solides</span>
          <span className="hidden sm:inline">Votre dossier présente des angles solides</span>
        </div>
        <div className="text-xs sm:text-[13px] leading-snug" style={{ color: '#4b5563' }}>
          <span className="sm:hidden">Montant non détaillé, aucune facture jointe.</span>
          <span className="hidden sm:inline">
            Le montant réclamé n'est pas détaillé et aucune facture n'est jointe. Deux éléments vous
            permettent d'exiger des justificatifs avant tout paiement.
          </span>
        </div>
      </div>
    </div>

    <div className="flex flex-col gap-2 px-4 pb-4 sm:gap-[9px] sm:px-6 sm:pb-5">
      {FINDINGS.map((f) => {
        const Icon = f.icon;
        return (
          <div
            key={f.titre}
            className="flex gap-[9px] rounded-lg border p-3 sm:gap-[11px] sm:px-[14px] sm:py-[13px]"
            style={{ backgroundColor: f.bg, borderColor: f.border }}
          >
            <Icon size={17} className="mt-[1px] shrink-0" style={{ color: f.iconColor }} />
            <div className="flex flex-col gap-[2px]">
              <div className="text-[13px] font-semibold" style={{ color: f.titleColor }}>{f.titre}</div>
              <div className="text-[12.5px] leading-snug" style={{ color: f.textColor }}>{f.texte}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default ReportPreviewCard;
